import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useZoho } from '../../context';
import { apiMailboxConnect } from '../../api';
import { CheckCircle, AlertCircle } from 'lucide-react';

/**
 * Where Zoho sends the browser back after consent.
 *
 * Zoho returns only an authorization code. Which region it was issued in, and
 * what the operator wanted to call this login, are both needed to finish the
 * exchange and neither survives the round trip — Zoho's `state` parameter is
 * not carried consistently across its regional variants. So they are parked in
 * sessionStorage on the way out and collected here.
 *
 * Nothing decides here whether this is a new login or a reconnect. The server
 * works that out by discovering the addresses the token carries and matching
 * them against existing mailboxes, which is the only way to get it right —
 * asking the operator, and having them answer wrong, produces two connections
 * holding the same mailboxes with only one of them being refreshed.
 */
function Redirect() {
  const navigate = useNavigate();
  const { jwtToken, user, loading: authLoading } = useAuth();
  const { refresh: refreshZoho } = useZoho();
  const [status, setStatus] = useState('working');
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const hasAttempted = useRef(false);

  useEffect(() => {
    if (authLoading) return;
    if (!jwtToken || user?.role !== 'admin') {
      setStatus('unauthorized');
      return;
    }
    if (hasAttempted.current) return;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');

    // Zoho reports a refused consent as an error parameter, not an absent code.
    // Saying "missing authorization code" for a deliberate cancel would send
    // someone hunting for a bug that is not there.
    const denied = params.get('error');
    if (denied) {
      setStatus('error');
      setError(
        denied === 'access_denied'
          ? 'Authorization was declined at the Zoho consent screen. Nothing was changed.'
          : `Zoho returned an error: ${denied}`
      );
      return;
    }
    if (!code) {
      setStatus('error');
      setError('Missing authorization code in the URL.');
      return;
    }

    hasAttempted.current = true;

    let intent = { region: 'com', label: '' };
    try {
      intent = { ...intent, ...JSON.parse(sessionStorage.getItem('mailbox_connect') || '{}') };
    } catch { /* a missing or malformed hint just means the defaults */ }

    (async () => {
      try {
        const data = await apiMailboxConnect(jwtToken, { code, region: intent.region, label: intent.label });
        sessionStorage.removeItem('mailbox_connect');
        setResult(data);
        // Critical: refresh the Zoho context so /mailer sees connected=true.
        // Without this the context still holds the value it captured when the
        // app remounted after Zoho's redirect, and the Mailer page shows the
        // "Connect" screen again.
        await refreshZoho();
        setStatus('success');
        setTimeout(() => navigate('/mailer', { replace: true }), 1400);
      } catch (e) {
        setStatus('error');
        setError(e.message);
      }
    })();
  }, [jwtToken, user, authLoading, navigate, refreshZoho]);

  const Card = ({ children }) => (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', padding: 16 }}>
      <div className="card" style={{ maxWidth: 460, textAlign: 'center' }}>{children}</div>
    </div>
  );

  if (status === 'working') {
    return (
      <Card>
        <span className="spinner" style={{ margin: '0 auto 16px', width: 32, height: 32 }} />
        <h2 style={{ marginBottom: 6 }}>Connecting your mailbox…</h2>
        <p className="text-muted" style={{ fontSize: 13 }}>
          Saving the authorization and reading which addresses this login can send from.
        </p>
      </Card>
    );
  }

  if (status === 'success') {
    return (
      <Card>
        <CheckCircle size={48} color="var(--success)" style={{ margin: '0 auto 16px' }} />
        <h2 style={{ marginBottom: 6 }}>
          {result?.reconnected ? 'Reconnected' : 'Mailbox connected'}
        </h2>
        <p className="text-muted" style={{ fontSize: 13, lineHeight: 1.6 }}>
          {result?.reconnected
            ? 'This login was already set up, so its credential was replaced rather than duplicated. Its mailboxes are working again.'
            : `Found ${result?.discovered ?? 0} address${result?.discovered === 1 ? '' : 'es'}${result?.created ? `, added ${result.created} as mailbox${result.created === 1 ? '' : 'es'}` : ''}.`}
        </p>
        {result?.mailboxes?.length > 0 && (
          <p className="text-muted" style={{ fontSize: 12, marginTop: 8 }}>
            {result.mailboxes.join(' · ')}
          </p>
        )}
        <p className="text-muted" style={{ fontSize: 12, marginTop: 12 }}>
          Set each one a daily limit on the Mailboxes tab — warm a new domain up rather than opening
          at full volume.
        </p>
      </Card>
    );
  }

  if (status === 'unauthorized') {
    return (
      <Card>
        <AlertCircle size={48} color="var(--danger)" style={{ margin: '0 auto 16px' }} />
        <h2 style={{ marginBottom: 6 }}>Not Signed In</h2>
        <p className="text-muted" style={{ fontSize: 13, marginBottom: 16 }}>
          You must be signed in as an admin to complete Zoho authorization.
        </p>
        <button className="btn btn-primary" onClick={() => navigate('/login')}>Sign in</button>
      </Card>
    );
  }

  return (
    <Card>
      <AlertCircle size={48} color="var(--danger)" style={{ margin: '0 auto 16px' }} />
      <h2 style={{ marginBottom: 6 }}>Connection Failed</h2>
      <p className="text-muted" style={{ fontSize: 13, marginBottom: 16 }}>{error}</p>
      <button className="btn btn-primary" onClick={() => navigate('/mailer')}>Back to Mailer</button>
    </Card>
  );
}

export default Redirect;
